/**
 * /welcome — Light, premium entry experience.
 *
 * Replaces the dark cinematic LinkCityScene aesthetic with the same
 * white/light surface used in Account / Checkout / Confirmation. The
 * brand reads as one product across rider + entry.
 *
 * Structure:
 *   Large Link wordmark (centered)
 *   Eyebrow + Headline + Sub
 *   RouteIllustration (Home ●─── Work)
 *   CTA stack: Create Account (primary) · Sign In · Join Waitlist
 *   Instant-quote chip (kept — proven conversion lever)
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import LightShell, { RouteIllustration } from "@/apps/rider/components/LightShell";
import InstantEstimate from "@/apps/rider/components/InstantEstimate";

export default function Welcome() {
  const nav = useNavigate();
  return (
    <LightShell testId="welcome-page">
      <div className="space-y-2 text-center sm:text-left mt-2" data-testid="welcome-wordmark">
        <div className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]" data-testid="welcome-eyebrow">
          Welcome to Link
        </div>
        <h1
          className="font-serif text-[36px] sm:text-[44px] leading-[1.05] tracking-tight text-[#0A1128]"
          data-testid="welcome-headline"
        >
          Transportation that shows up.
        </h1>
        <p className="text-[15px] leading-relaxed text-[#515E7B] max-w-md mx-auto sm:mx-0 pt-1" data-testid="welcome-sub">
          Reliable pickup and return transportation for work, school, healthcare, travel, and everyday life.
        </p>
      </div>

      <div className="mt-7">
        <RouteIllustration origin="Home" destination="Work" testId="welcome-route" />
      </div>

      <div className="mt-7 space-y-2.5">
        <PrimaryCTA label="Create account" onClick={() => nav("/onboarding")} testId="welcome-cta-register" />
        <SecondaryCTA label="Sign in" onClick={() => nav("/login")} testId="welcome-cta-signin" />
        <TertiaryCTA label="Join waitlist" onClick={() => nav("/waitlist")} testId="welcome-cta-waitlist" />
      </div>

      <InstantEstimate visible delay={120} />
    </LightShell>
  );
}

function PrimaryCTA({ label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] active:bg-[#d75600] text-white text-[14px] tracking-[0.04em] font-semibold transition-colors flex items-center justify-center gap-2"
      data-testid={testId}
    >
      <span>{label}</span>
      <ArrowRight size={16} strokeWidth={2.2} className="transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function SecondaryCTA({ label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-14 rounded-2xl border border-[#0A1128]/15 hover:border-[#ed6500] text-[#0A1128] text-[14px] tracking-[0.04em] font-semibold transition-colors"
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function TertiaryCTA({ label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-12 text-[12px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] transition-colors"
      data-testid={testId}
    >
      {label}
    </button>
  );
}
